import { Progress, Skeleton } from "@mantine/core";
import type { SprintCapacitySummary } from "@/types/capacity";

interface SprintCapacityInlineProps {
	capacity: SprintCapacitySummary | null;
	variant: "card" | "row";
	loading?: boolean;
}

function barColor(assignedPct: number, isOverbooked: boolean) {
	if (assignedPct >= 75) return isOverbooked ? "red" : "orange";
	return "blue";
}

const SprintCapacityInline = ({
	capacity,
	variant,
	loading,
}: SprintCapacityInlineProps) => {
	if (loading) {
		if (variant === "row") {
			return (
				<div className="flex flex-col gap-1 min-w-[140px]">
					<Skeleton height={8} />
					<Skeleton height={10} width="50%" />
				</div>
			);
		}
		return (
			<div className="flex flex-col w-full mt-1 gap-1 items-start justify-start">
				<span className="flex items-center gap-1">
					<Skeleton height={10} width="80" />
					<Skeleton height={10} width="100" />
				</span>
				<Skeleton height={14} width="100%" />
			</div>
		);
	}

	if (!capacity) {
		if (variant === "row") {
			return (
				<span className="text-[10px] text-muted-foreground">
					No active sprint
				</span>
			);
		}
		return (
			<div className="w-full mt-4 text-left">
				<span className="text-secondary font-light text-[10px]">
					Sprint Capacity
				</span>
				<p className="text-[10px] text-muted-foreground mt-0.5">
					No active sprint
				</p>
			</div>
		);
	}

	const consumedHours = capacity.consumedHours ?? 0;
	const consumedPct = capacity.capacityHours
		? Math.min(
				Math.round((consumedHours / capacity.capacityHours) * 100),
				100,
			)
		: 0;
	const remainingPct = 100 - consumedPct;
	const color = barColor(consumedPct, capacity.isOverbooked);

	if (variant === "row") {
		return (
			<div className="flex flex-col gap-1 min-w-[140px] max-w-[180px]">
				<Progress.Root size="sm">
					<Progress.Section value={consumedPct} color={color} />
					<Progress.Section value={remainingPct} color="#b8bbc1" />
				</Progress.Root>
				<div className="flex items-center justify-between gap-2">
					<span className="text-[10px]">
						<b
							className={
								capacity.isOverbooked
									? "text-danger"
									: "text-primary"
							}
						>
							{consumedHours}h
						</b>
						<span className="text-muted-foreground">
							{" "}
							/ {capacity.capacityHours}h
						</span>
					</span>
					{capacity.isOverbooked && (
						<span className="text-[9px] text-danger font-medium">
							Overbooked
						</span>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col w-full mt-0 gap-1">
			<div className="flex items-center gap-1">
				<span className="text-secondary font-medium text-xs">
					Sprint Capacity
				</span>
				<span className="text-muted text-xs">|</span>
				<span className="text-xs text-muted-foreground truncate">
					{capacity.sprintName}
				</span>
				<div className="flex flex-col items-center ml-auto">
					<div className="text-xs">
						<b
							className={
								capacity.isOverbooked
									? "text-danger"
									: "text-primary"
							}
						>
							{consumedHours}h
						</b>
						<span className="text-muted-foreground">
							{" "}
							/ {capacity.capacityHours}h
						</span>
					</div>
					{capacity.isOverbooked && (
						<span className="text-[9px] text-danger font-medium">
							Overbooked
						</span>
					)}
				</div>
			</div>

			<Progress.Root size="lg">
				<Progress.Section value={consumedPct} color={color} animated>
					<Progress.Label>{consumedPct}%</Progress.Label>
				</Progress.Section>
				<Progress.Section value={remainingPct} color="#b8bbc1">
					<Progress.Label>{remainingPct}%</Progress.Label>
				</Progress.Section>
			</Progress.Root>
		</div>
	);
};

export default SprintCapacityInline;
